import { createReducer, createActions } from 'reduxsauce'
import Immutable from 'seamless-immutable'
import { layers } from '../config/services'
import * as paints from '../layers/paints'
import defaultDatetimeAccessor from '../utility/defaultDatetimeAccessor'

// Redux type and action creators
const { Types, Creators } = createActions({
  layerUpdateRequested: ['layer'],
  layerUpdateFulfilled: ['datetime'],
  layerUpdateFailed: ['error'],
  layerRenderToggled: ['name'],
  layerPaintUpdated: ['paint'],
  layerFilterUpdated: ['layerName', 'filter'],
  layerTimeUpdated: ['layerName', 'timestamp'],
  layerTimeToggled: ['layerName', 'timestamp'],
  layerDefaultTimeUpdated: ['layerName', 'json', 'accessor'],
  layerTransparencyUpdated: ['layerName', 'layerOpacity', 'transparency'],
  layerSelectedUpdated: ['selected'],
  layersReset: [],
  reset: []
})

export const LayerTypes = Types
export default Creators

// Initial state
export const INITIAL_STATE = Immutable({
  fetching: false,
  loaded: false,
  layers: layers,
  layersRendered: [
    // 'SmartIce',
    'SmartIceQamutikLineString',
    'SmartIceFeatures',
    'RiverIce',
    'RiverIceChurchill',
    'RiverIceMarkers',
    'ChurchillMarkers',
    'ChurchillMask',
    'LandDeformationInsar',
    // 'MopraWellsites',
    'WellsiteRank',
    'WellsiteNdviLease',
    // 'WellsiteNdviLeaseSimple',
    // 'LandCover2010',
    // 'MopraVector',
    'Buoys',
    'WaterTemperature',
    'WaterSediment',
    'WaterLakeTss',
    // 'IceDrift',
    'IceDriftVector',
    // 'IceDriftVelocity',
    'Sentinel1Wms',
    'BriggsIceType',
    'BriggsIceDepth',
    'InfrastructurePipelineVector',
    'Infrastructure',
    'FloodRiverClassification',
    'FloodHazard',
    'BadgerMask',
    'ExploitsMarkers',
    'RiverFloodMonitoring',
    'LeakDetection',
    'UploadRaster',
    'ArcticCrowdSource',
    'PipelineChangeDetection',
    'PipelineMask',
    'Sentinel2PipelineChangeDetectionBase',
    'Sentinel2PipelineChangeDetectionYann',
    'Sentinel2PipelineChangeDetection',
    'Sentinel2EnbridgePipeline',
    // 'Sentinel2Transparent',
    'BathymetryWavelet',
    'BathymetryFft',
    'PlanetLabs',
    'PlanetLabsPlusPlus',
    'PlanetScope',
    'PipelineBasic',
    // 'Sentinel2Pipeline',
    'Sentinel1Pipeline',
    'EnbridgePipeline',
    // 'PlanetMosaic',
    'PipelineMonthly',
    'Wetlands',
    'EnergirChange',
    'Energir',
    'EnergirMeasures',
    'Petrobras',
    'PetrobrasDrone'
  ],
  paints: paints,
  filters: {},
  selected: {},
  times: {},
  prevTimes: {},
  transparencies: {},
  error: null
})

// Redux reducers
export const layerUpdateRequested = state => {
  return state.merge({ fetching: true })
}

export const layerUpdateFulfilled = (state, { datetime }) => {
  // const timestamps = json.features.map(feature => feature.properties.timestamp)
  return state
}

export const layerUpdateFailed = (state, { error }) => {
  console.warn(error)
  return state.merge({ error, fetching: false })
}

export const layerPaintUpdated = (state, { layerName, paint }) => {
  const paints = state.paints.set(layerName, paint)
  return state.merge({ paints })
}

export const layerFilterUpdated = (state, { layerName, filter }) => {
  const filters = state.filters.set(layerName, filter)

  return state.merge({ filters })
}

export const layerTimeUpdated = (state, { layerName, timestamp }) => {
  const prevTimestamp = state.times[layerName]
  const prevTimes = state.prevTimes.set(layerName, prevTimestamp)
  const times = state.times.set(layerName, timestamp)
  return state.merge({ times, prevTimes })
}

export const layerTimeToggled = (state, { layerName, timestamp }) => {
  const currentTimestamp = state.times[layerName]
  let prevTimestamp = state.prevTimes[layerName]

  const nextTimestamp = timestamp !== currentTimestamp
    ? timestamp
    : prevTimestamp
  prevTimestamp = timestamp !== currentTimestamp
    ? currentTimestamp
    : timestamp

  const prevTimes = state.prevTimes.set(layerName, prevTimestamp)
  const times = state.times.set(layerName, nextTimestamp)
  return state.merge({ times, prevTimes })
}

export const layerDefaultTimeUpdated = (
  state,
  { layerName, json, accessor = defaultDatetimeAccessor }
) => {
  if (!json) {
    return state
  }

  if (state.times[layerName]) {
    return state
  } else {
    const timestampArray = accessor(json).sort()
    if (!timestampArray.length) {
      return state
    } else {
      const timestamp = new Date(
        timestampArray.slice().sort()[timestampArray.length - 1]
      ).getTime()      
      const times = state.times.set(layerName, timestamp)
      return state.merge({ times })
    }
  }
}

export const layerTransparencyUpdated = (
  state,
  { layerName, layerOpacity, transparency }
) => {
  const transparencies = state.transparencies.set(layerName, transparency)
  const paints = state.paints.setIn([layerName, layerOpacity], transparency)

  return state.merge({ transparencies, paints })
}

export const layerRenderToggled = (state, { name }) => {
  const { layersRendered } = state
  const nextLayersRendered =
    layersRendered.indexOf(name) >= 0
      ? layersRendered.filter(layer => layer !== name)
      : [name, ...layersRendered]
  return state.merge({ layersRendered: nextLayersRendered })
}

export const layerSelectedUpdated = (state, { selected }) => {
  return state
}

export const layersReset = state => {
  return INITIAL_STATE
}

// Assign types to reducers
export const reducer = createReducer(INITIAL_STATE, {
  [Types.LAYER_UPDATE_REQUESTED]: layerUpdateRequested,
  [Types.LAYER_UPDATE_FULFILLED]: layerUpdateFulfilled,
  [Types.LAYER_UPDATE_FAILED]: layerUpdateFailed,
  [Types.LAYER_RENDER_TOGGLED]: layerRenderToggled,
  [Types.LAYER_PAINT_UPDATED]: layerPaintUpdated,
  [Types.LAYER_FILTER_UPDATED]: layerFilterUpdated,
  [Types.LAYER_TIME_UPDATED]: layerTimeUpdated,
  [Types.LAYER_TIME_TOGGLED]: layerTimeToggled,
  [Types.LAYER_DEFAULT_TIME_UPDATED]: layerDefaultTimeUpdated,
  [Types.LAYER_TRANSPARENCY_UPDATED]: layerTransparencyUpdated,
  [Types.LAYER_SELECTED_UPDATED]: layerSelectedUpdated,
  [Types.LAYERS_RESET]: layersReset,
  [Types.RESET]: layersReset
})



// WEBPACK FOOTER //
// ./src/redux/layerRedux.js